// src/components/website/EventsSection.js
// Events section wrapper for published practitioner websites.
// Fetches published events, filters to upcoming only, provides list/calendar toggle.

import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase-config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import EventListView from './EventListView';
import EventCalendarView from './EventCalendarView';
import { List, CalendarDays } from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getEventSortDate(event) {
  const { eventType, schedule } = event;
  if (eventType === 'single' && schedule?.date) return schedule.date;
  if (eventType === 'series' && schedule?.sessionDates?.[0]) return schedule.sessionDates[0];
  if (eventType === 'multi_day' && schedule?.date) return schedule.date;
  return '9999-12-31';
}

function getEventEndDate(event) {
  const { eventType, schedule } = event;
  if (eventType === 'single' && schedule?.date) return schedule.date;
  if (eventType === 'series' && schedule?.sessionDates?.length > 0) {
    return schedule.sessionDates[schedule.sessionDates.length - 1];
  }
  if (eventType === 'multi_day' && schedule?.endDate) return schedule.endDate;
  return '0000-01-01';
}

// ─── Component ──────────────────────────────────────────────────────────────

const EventsSection = ({ practitionerId, practitionerSlug, settings, headingStyle }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(settings?.defaultView || 'list');

  useEffect(() => {
    if (!db || !practitionerId) { setLoading(false); return; }

    (async () => {
      try {
        const q = query(
          collection(db, 'practitioners', practitionerId, 'events'),
          where('status', '==', 'published'),
        );
        const snap = await getDocs(q);
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Filter out past events (event has fully ended)
        const today = new Date().toISOString().split('T')[0];
        const upcoming = all
          .filter(e => getEventEndDate(e) >= today)
          .sort((a, b) => getEventSortDate(a).localeCompare(getEventSortDate(b)));

        setEvents(upcoming);
      } catch (err) {
        console.warn('EventsSection: Failed to load events:', err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [practitionerId]);

  // Don't render section if no upcoming events (or still loading)
  if (loading || events.length === 0) return null;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', width: '100%' }}>
      {/* Title + view toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <h2 style={{ ...headingStyle, margin: 0 }}>
          {settings?.sectionTitle || 'Upcoming Events'}
        </h2>

        {/* View toggle */}
        <div style={{ display: 'flex', gap: 4, background: '#F3F4F6', borderRadius: 8, padding: 3 }}>
          <span
            role="button"
            tabIndex={0}
            onClick={() => setView('list')}
            onKeyDown={(e) => e.key === 'Enter' && setView('list')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 30, borderRadius: 6, cursor: 'pointer', userSelect: 'none',
              background: view === 'list' ? '#FFFFFF' : 'transparent',
              boxShadow: view === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.15s',
            }}
            title="List view"
          >
            <List size={16} color={view === 'list' ? '#7c3aed' : '#9CA3AF'} />
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => setView('calendar')}
            onKeyDown={(e) => e.key === 'Enter' && setView('calendar')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 30, borderRadius: 6, cursor: 'pointer', userSelect: 'none',
              background: view === 'calendar' ? '#FFFFFF' : 'transparent',
              boxShadow: view === 'calendar' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.15s',
            }}
            title="Calendar view"
          >
            <CalendarDays size={16} color={view === 'calendar' ? '#7c3aed' : '#9CA3AF'} />
          </span>
        </div>
      </div>

      {/* Content */}
      {view === 'list' ? (
        <EventListView events={events} practitionerSlug={practitionerSlug} />
      ) : (
        <EventCalendarView events={events} practitionerSlug={practitionerSlug} />
      )}
    </div>
  );
};

export default EventsSection;
